import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import {
  pathBomStockStatusLabel,
  PATH_BOM_STOCK_STATUS,
} from "../utils/pathBomStockCheck";
import {
  FORM_CANCEL_BUTTON_SX,
  FORM_SUBMIT_BUTTON_SX,
  STANDARD_FORM_DIALOG_ACTIONS_SX,
  STANDARD_FORM_DIALOG_PAPER_SX,
  STANDARD_FORM_DIALOG_TITLE_SX,
} from "../utils/formModalStyles";

/**
 * Review popup when pathology BOM stock is sufficient — shows available qty,
 * batch, and expiry (similar to medicine issue review) before deduct + collect.
 */
const PathBomStockReviewDialog = ({
  open,
  onClose,
  onConfirm,
  checks = [],
  confirming = false,
  title = "Review pathology consumable stock",
}) => {
  const rows = Array.isArray(checks) ? checks : [];

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : onClose}
      maxWidth="md"
      fullWidth
      sx={STANDARD_FORM_DIALOG_PAPER_SX}
    >
      <DialogTitle sx={STANDARD_FORM_DIALOG_TITLE_SX}>{title}</DialogTitle>
      <DialogContent>
        <Alert severity="success" sx={{ mb: 2 }}>
          <AlertTitle>Stock is sufficient</AlertTitle>
          Review available quantity and batch details below. Confirm to deduct
          inventory and collect the sample(s).
        </Alert>

        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No consumable details to review.
          </Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Consumable</TableCell>
                  <TableCell>Lab test(s)</TableCell>
                  <TableCell align="right">Need</TableCell>
                  <TableCell align="right">Available</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Batch / Expiry</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((check, index) => {
                  const batches = Array.isArray(check.usableBatches)
                    ? check.usableBatches
                    : [];
                  const fefo = batches[0];
                  return (
                    <TableRow key={`${check.bomId ?? check.itemName}-${index}`} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {check.itemName || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {(check.labTestNames || []).join(", ") || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{check.requiredQty ?? 0}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} color="success.main">
                          {check.availableQty ?? 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={pathBomStockStatusLabel(
                            check.status || PATH_BOM_STOCK_STATUS.OK
                          )}
                          color="success"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {batches.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            No usable batches
                          </Typography>
                        ) : (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                            {fefo && (
                              <Box>
                                <Typography variant="caption" display="block" fontWeight={600}>
                                  FEFO batch (will be used first)
                                </Typography>
                                <Typography variant="caption" display="block">
                                  <strong>Batch:</strong> {fefo.batchNumber || "N/A"}
                                  {" · "}
                                  <strong>Qty:</strong> {fefo.quantity}
                                  {fefo.unitType ? ` ${fefo.unitType}` : ""}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Expiry:{" "}
                                  {fefo.expiryDate
                                    ? dayjs(fefo.expiryDate).format("DD MMM YYYY")
                                    : "N/A"}
                                </Typography>
                              </Box>
                            )}
                            {batches.slice(1, 4).map((batch, bIndex) => (
                              <Box key={`${batch.inventoryId}-${bIndex}`}>
                                <Typography variant="caption" display="block">
                                  <strong>Batch:</strong> {batch.batchNumber || "N/A"}
                                  {" · "}
                                  <strong>Qty:</strong> {batch.quantity}
                                  {batch.unitType ? ` ${batch.unitType}` : ""}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Expiry:{" "}
                                  {batch.expiryDate
                                    ? dayjs(batch.expiryDate).format("DD MMM YYYY")
                                    : "N/A"}
                                </Typography>
                              </Box>
                            ))}
                            {batches.length > 4 && (
                              <Typography variant="caption" color="text.secondary">
                                +{batches.length - 4} more batch(es)
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={STANDARD_FORM_DIALOG_ACTIONS_SX}>
        <Button onClick={onClose} disabled={confirming} sx={FORM_CANCEL_BUTTON_SX}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={confirming || rows.length === 0}
          sx={FORM_SUBMIT_BUTTON_SX}
          startIcon={confirming ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {confirming ? "Deducting…" : "Confirm & Collect"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PathBomStockReviewDialog;
